// src/pages/Results.js
import { useEffect, useState } from 'react';
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Cell
} from 'recharts';
import { collection, query, where, getDocs, doc, getDoc } from 'firebase/firestore/lite';
import { db } from '../firebase/config';
import { useAuth } from '../context/AuthContext';
import { DEFAULT_CATEGORIES, PERSONALITY_TYPES, withCurrentLabels } from '../utils/surveyQuestions';
import PageLoading from '../components/PageLoading';

const COLORS = ['#4ECDC4', '#FF6B6B', '#FFE66D', '#A8E6CF', '#C3B1E1', '#F7A072'];

function scoreColor(score) {
  if (score >= 4) return '#2d6a4f';
  if (score >= 3) return '#f4a261';
  return '#e63946';
}

function scoreLabel(score) {
  if (score >= 4.5) return { label: 'Excellent', cls: 'badge-green' };
  if (score >= 3.5) return { label: 'Good', cls: 'badge-blue' };
  if (score >= 2.5) return { label: 'Needs attention', cls: 'badge-yellow' };
  return { label: 'Critical', cls: 'badge-red' };
}

function computeScores(responses, categories) {
  const catScores = {};
  categories.forEach(cat => {
    const scores = [];
    responses.forEach(r => {
      cat.questions.forEach(q => {
        if (r.answers?.[q.id] != null) scores.push(r.answers[q.id]);
      });
    });
    catScores[cat.id] = scores.length
      ? +(scores.reduce((a, b) => a + b, 0) / scores.length).toFixed(2)
      : null;
  });
  return catScores;
}

export default function Results() {
  const { userProfile, effectiveUid } = useAuth();
  const [responses, setResponses] = useState([]);
  const [categories, setCategories] = useState(DEFAULT_CATEGORIES);
  const [loading, setLoading] = useState(true);
  const [openEnded, setOpenEnded] = useState([]);
  const [personalityTypeCounts, setPersonalityTypeCounts] = useState({});

  const role = userProfile?.role || 'free';
  const teamId = userProfile?.teamId;
  const isTeamView = ['team_leader', 'team_member', 'consultant', 'admin'].includes(role) && !!teamId;

  useEffect(() => {
    loadResults();
  }, [userProfile, effectiveUid]);

  async function loadResults() {
    try {
      let q;
      if (isTeamView && teamId) {
        q = query(collection(db, 'responses'), where('teamId', '==', teamId));
      } else {
        q = query(collection(db, 'responses'), where('userId', '==', effectiveUid));
      }
      const snap = await getDocs(q);
      const data = snap.docs.map(d => d.data());
      setResponses(data);

      // Collect open-ended
      const oe = data.flatMap(r =>
        Object.entries(r.openEnded || {})
          .filter(([, v]) => v?.trim())
          .map(([k, v]) => ({ key: k, text: v }))
      );
      setOpenEnded(oe);

      // Aggregate personality types
      const typeCounts = {};
      PERSONALITY_TYPES.forEach(t => { typeCounts[t.name] = 0; });
      data.forEach(r => {
        (r.teamDynamics?.personalityTypes || []).forEach(name => {
          if (typeCounts[name] !== undefined) typeCounts[name]++;
        });
      });
      setPersonalityTypeCounts(typeCounts);

      // Custom questions for team
      if (teamId) {
        const teamSnap = await getDoc(doc(db, 'teams', teamId));
        if (teamSnap.exists() && teamSnap.data().customQuestions) {
          setCategories(withCurrentLabels(teamSnap.data().customQuestions));
        }
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <PageLoading text="Loading results..." />;

  if (responses.length === 0) {
    return (
      <div className="fade-up">
        <div className="page-header">
          <h2>No results yet</h2>
          <p>
            {isTeamView
              ? 'Results will appear here once team members have submitted their assessments.'
              : 'Complete your assessment to see your results.'}
          </p>
        </div>
      </div>
    );
  }

  const catScores = computeScores(responses, categories);
  const overallScore = +(Object.values(catScores).filter(Boolean).reduce((a, b) => a + b, 0) / Object.values(catScores).filter(Boolean).length).toFixed(2);

  const radarData = categories.map(cat => ({
    category: cat.label,
    score: catScores[cat.id] || 0,
    fullMark: 5,
  }));

  const barData = categories.map((cat, i) => ({
    name: cat.label.split(' ').slice(0, 2).join(' '),
    score: catScores[cat.id] || 0,
    color: COLORS[i],
  }));

  const { label: overallLabel, cls: overallCls } = scoreLabel(overallScore);
  const openEndedKeys = { strengths: 'Strengths', challenges: 'Challenges', suggestions: 'Suggestions' };

  return (
    <div className="fade-up">
      <div className="page-header">
        <h2>{isTeamView ? 'Team Health Results' : 'Your Assessment Results'}</h2>
        <p>
          {isTeamView
            ? `Based on ${responses.length} response${responses.length !== 1 ? 's' : ''} from your team`
            : 'Your personal health snapshot'}
        </p>
      </div>

      {/* Overall score */}
      <div style={{
        background: 'var(--ink)',
        borderRadius: 'var(--radius-lg)',
        padding: '36px 40px',
        marginBottom: 28,
        display: 'flex',
        alignItems: 'center',
        gap: 32,
        flexWrap: 'wrap',
      }}>
        <div>
          <div style={{ fontSize: '0.75rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)', marginBottom: 8 }}>
            Overall Team Health Score
          </div>
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '4.5rem', color: 'white', lineHeight: 1 }}>
            {overallScore}
            <span style={{ fontSize: '1.5rem', color: 'rgba(255,255,255,0.3)' }}>/5</span>
          </div>
        </div>
        <div>
          <span className={`badge ${overallCls}`} style={{ fontSize: '0.85rem', padding: '6px 16px' }}>
            {overallLabel}
          </span>
          {isTeamView && (
            <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.3)', marginTop: 10 }}>
              {responses.length} responses collected
            </div>
          )}
        </div>
      </div>

      {/* Charts */}
      <div className="grid-2" style={{ marginBottom: 28 }}>
        {/* Radar */}
        <div className="card">
          <div className="card-title">Category Overview</div>
          <div className="card-subtitle">Radar view across all 6 dimensions</div>
          <ResponsiveContainer width="100%" height={280}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="#e5e7eb" />
              <PolarAngleAxis dataKey="category" tick={{ fontSize: 11, fill: '#8888aa' }} />
              <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fontSize: 10 }} />
              <Radar
                name="Score"
                dataKey="score"
                stroke="#2d6a4f"
                fill="#2d6a4f"
                fillOpacity={0.25}
                strokeWidth={2}
              />
            </RadarChart>
          </ResponsiveContainer>
        </div>

        {/* Bar chart */}
        <div className="card">
          <div className="card-title">Score by Category</div>
          <div className="card-subtitle">1 = very low · 5 = excellent</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={barData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0eee8" />
              <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#8888aa' }} />
              <YAxis domain={[0, 5]} tick={{ fontSize: 10 }} />
              <Tooltip
                formatter={(val) => [val.toFixed(2), 'Score']}
                contentStyle={{ borderRadius: 8, border: '1px solid var(--border)', fontSize: 12 }}
              />
              <Bar dataKey="score" radius={[6, 6, 0, 0]}>
                {barData.map((entry, i) => (
                  <Cell key={i} fill={entry.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Category breakdown */}
      <div className="card" style={{ marginBottom: 28 }}>
        <div className="card-title">Category Breakdown</div>
        <div className="card-subtitle">Detailed score with health status for each dimension</div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 8 }}>
          {categories.map((cat, i) => {
            const score = catScores[cat.id];
            const { label, cls } = scoreLabel(score || 0);
            const pct = score ? (score / 5) * 100 : 0;
            return (
              <div key={cat.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 10, height: 10, borderRadius: '50%', background: cat.color }} />
                    <span style={{ fontWeight: 500, fontSize: '0.9rem' }}>{cat.label}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className={`badge ${cls}`}>{label}</span>
                    <span style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', color: scoreColor(score || 0) }}>
                      {score?.toFixed(1) || '—'}
                    </span>
                  </div>
                </div>
                <div className="progress-bar">
                  <div className="progress-fill" style={{ width: `${pct}%`, background: cat.color }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Open-ended feedback */}
      {openEnded.length > 0 && (
        <div className="card">
          <div className="card-title">Written Feedback</div>
          <div className="card-subtitle">
            {isTeamView ? 'Anonymous written responses from your team' : 'Your written responses'}
          </div>

          {['strengths', 'challenges', 'suggestions'].map(key => {
            const items = openEnded.filter(o => o.key === key);
            if (items.length === 0) return null;
            return (
              <div key={key} style={{ marginTop: 24 }}>
                <h4 style={{ fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--ink-muted)', marginBottom: 12 }}>
                  {openEndedKeys[key]}
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {items.map((item, i) => (
                    <div key={i} style={{
                      background: 'var(--bg-soft)',
                      borderRadius: 8,
                      padding: '12px 16px',
                      fontSize: '0.875rem',
                      color: 'var(--ink-soft)',
                      borderLeft: '3px solid var(--accent)',
                      lineHeight: 1.6,
                    }}>
                      "{item.text}"
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Team Dynamics — personality types */}
      {(() => {
        const observed = PERSONALITY_TYPES.filter(t => personalityTypeCounts[t.name] > 0);
        if (observed.length === 0) return null;
        const maxCount = Math.max(...observed.map(t => personalityTypeCounts[t.name]));
        return (
          <div className="card" style={{ marginTop: 28 }}>
            <div className="card-title">Team Dynamics</div>
            <div className="card-subtitle">
              {isTeamView
                ? 'Meeting personality types observed by respondents'
                : 'Meeting personality types you observed on the team'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 20 }}>
              {observed.map(type => {
                const count = personalityTypeCounts[type.name];
                const intensity = count / maxCount;
                return (
                  <div
                    key={type.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '10px 14px',
                      borderRadius: 8,
                      background: `rgba(45,106,79,${0.05 + intensity * 0.12})`,
                      border: `1.5px solid rgba(45,106,79,${0.15 + intensity * 0.45})`,
                    }}
                  >
                    <span style={{ fontWeight: 700, color: 'var(--ink)', minWidth: 130 }}>{type.name}</span>
                    <span style={{ fontSize: '0.82rem', color: 'var(--ink-muted)', flex: 1 }}>{type.description}</span>
                    {isTeamView && (
                      <span style={{
                        fontSize: '0.78rem',
                        fontWeight: 700,
                        background: 'rgba(45,106,79,0.15)',
                        color: '#2d6a4f',
                        borderRadius: 10,
                        padding: '2px 8px',
                        whiteSpace: 'nowrap',
                      }}>
                        {count} {count === 1 ? 'respondent' : 'respondents'}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            <p style={{ marginTop: 14, fontSize: '0.75rem', color: 'var(--ink-muted)' }}>
              {isTeamView
                ? 'Darker rows indicate types observed by more respondents.'
                : 'Types you selected during your assessment.'}
            </p>
          </div>
        );
      })()}
    </div>
  );
}
